const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype;

const capitalize = (key: string): string =>
  key.length === 0 ? key : `${key[0].toUpperCase()}${key.slice(1)}`;

const stringifyPrimitive = (
  value: string | number | boolean | bigint | null,
): string => (value === null ? "" : String(value));

const flatten = (
  input: Record<string, unknown>,
  prefix: string,
  acc: Record<string, string>,
): void => {
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined) {
      continue;
    }

    const flatKey = prefix === "" ? key : `${prefix}${capitalize(key)}`;

    if (isPlainObject(value)) {
      flatten(value, flatKey, acc);
      continue;
    }

    if (
      value === null ||
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean" ||
      typeof value === "bigint"
    ) {
      acc[flatKey] = stringifyPrimitive(value);
    }
  }
};

/**
 * Converte um objeto (possivelmente aninhado) em uma string JSON achatada
 * onde todas as chaves estão na raiz, concatenadas em camelCase, e todos os
 * valores são strings.
 *
 * Exemplo: `nomeCompleto.nome = "Lucas"` vira `nomeCompletoNome = "Lucas"`.
 *
 * Valores `undefined` são ignorados. `null` vira string vazia. Tipos não
 * primitivos e que não sejam plain objects (Blob, Date, Function, etc.) são
 * ignorados.
 */
export const flattenToJsonString = (input: unknown): string => {
  if (!isPlainObject(input)) {
    return "{}";
  }

  const acc: Record<string, string> = {};
  flatten(input, "", acc);
  return JSON.stringify(acc);
};
